// Library methods
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// Components
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import { MenuIcon } from "src/assets/icons";

// Styles
import {
  getSurfaceTokens,
  getPopoverPaperSx,
} from "src/styles/mui/muiStylesHelper";

type NavbarMobileMenuType = {
  navbarPages: Array<{ title: string; path: string; showTab: boolean }>;
};

const NavbarMobileMenu = ({ navbarPages }: NavbarMobileMenuType) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);

  const filteredNavbarPages = useMemo(
    () => navbarPages.filter((page) => page.showTab),
    [navbarPages]
  );

  const [menu, setMenu] = useState<HTMLButtonElement | null>(null);

  const menuClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setMenu(event.currentTarget);

  // menu close handler
  const menuClose = () => setMenu(null);

  // close the menu on window resize
  useEffect(() => {
    const handleResize = () => menuClose();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      {/* page switcher button */}
      <IconButton
        onClick={menuClick}
        sx={{
          display: "flex",
          color: "#FFF",
          fontSize: "20px",
        }}
      >
        <MenuIcon />
      </IconButton>

      {/* page switcher menu, when open */}
      <Popover
        sx={{ mt: "16px" }}
        open={Boolean(menu)}
        anchorEl={menu}
        onClose={menuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          style: {
            width: "100%",
            padding: "8px",
            borderRadius: "4px",
            ...getPopoverPaperSx(theme),
          },
        }}
      >
        <Box display="flex" gap="4px" flexDirection="column" width="100%">
          {filteredNavbarPages.map((navbarPage) => (
            <Button
              key={navbarPage.title}
              onClick={() => {
                navigate(navbarPage.path);
                menuClose();
              }}
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                color: onSurface,
                textTransform: "capitalize",
                fontSize: "16px",
                "&:hover": {
                  backgroundColor: "#5B6D7A20",
                },
              }}
            >
              {navbarPage.title}
            </Button>
          ))}
        </Box>
      </Popover>
    </>
  );
};

export default NavbarMobileMenu;
